import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import ArticleCard from "@/components/base/ArticleCard";
import ScrollToTop from "@/components/base/ScrollToTop";
import { getArticleById, articles } from "@/mocks/articles";
import { categoryMap } from "@/mocks/categories";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <main className="pt-20 px-6 md:px-10 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1
              className="text-2xl font-bold text-foreground-950 mb-3"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              文章未找到
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              您访问的文章不存在或已被移除
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const categoryName = categoryMap[article.category] || article.category;

  const relatedArticles = articles
    .filter(
      (a) => a.category === article.category && a.id !== article.id
    )
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    author: {
      "@type": "Person",
      name: article.author,
    },
    publisher: {
      "@type": "Organization",
      name: "苍井优中文资讯站",
    },
    url: `https://yuaoi-fans.com/article/${article.id}`,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `https://yuaoi-fans.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "https://yuaoi-fans.com/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: categoryName,
        item: `https://yuaoi-fans.com/category/${article.category}`,
      },
      {
        "@type": "ListItem",
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <section className="w-full px-6 md:px-10 py-6 bg-background-100">
            <Breadcrumb
              items={[
                { label: "首页", href: "/" },
                {
                  label: categoryName,
                  href: `/category/${article.category}`,
                },
                { label: article.title },
              ]}
            />
          </section>

          <article className="w-full max-w-3xl mx-auto px-6 md:px-10 py-8 md:py-12">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-3 mb-4 flex-wrap">
                <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 whitespace-nowrap">
                  {categoryName}
                </span>
                <span className="text-xs text-foreground-400">
                  {article.date}
                </span>
                <span className="text-xs text-foreground-400">
                  {article.author}
                </span>
              </div>
              <h1
                className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 leading-tight mb-4"
                style={{ fontFamily: "var(--font-heading)" }}
              >
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 leading-relaxed">
                {article.excerpt}
              </p>
            </header>

            {/* Feature Image */}
            <figure className="mb-10">
              <div className="w-full aspect-[16/9] rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.imageAlt}
                  title={article.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </figure>

            {/* Content */}
            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/60">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-xs px-2.5 py-1 rounded-full bg-background-100 text-foreground-600 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </article>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-6 md:px-10 py-12 md:py-16 bg-background-100">
              <div className="max-w-6xl mx-auto">
                <h2
                  className="text-xl md:text-2xl font-bold text-foreground-950 mb-6"
                  style={{ fontFamily: "var(--font-heading)" }}
                >
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {relatedArticles.map((rel) => (
                    <ArticleCard key={rel.id} article={rel} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Back Navigation */}
          <section className="w-full px-6 md:px-10 py-6">
            <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-line"></i>
                返回{categoryName}
              </Link>
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                返回首页
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}